import { guard, sample } from 'effector';
import { request } from 'libs/request';
import { baseUrls$ } from 'model/config.model';
import { RootDomain } from 'model/root.model';
import { applicationKey$, sessionConfig$ } from 'model/session.model';

export type WidgetInitializedEvent = {
	event: 'widget_initialized';
	event_attributes: {
		implementation_type: 'inline' | 'modal';
	}
}
export type WelcomeScreenShown = {
	event: 'welcome_screen_shown';
	event_attributes: {
		type: 'desktop' | 'mobile' | 'handoff';
		ua: string;
		geoip_country: string;
		language: string;
		from: string | null;
	}
}
export type WelcomeScreenContinue = {
	event: 'welcome_screen_continue';
	event_attributes: {
		type: 'desktop' | 'mobile' | 'handoff';
		duration: number;
	}
}
export type QrScreenShown = { event: 'qr_screen_shown'; }
export type LinkScreenShown = { event: 'link_screen_shown'; }
export type LinkCopied = {
	event: 'link_copied';
	event_attributes: {
		duration: number;
	}
}

export type EmailScreenShown = { event: 'email_screen_shown'; }
export type EmailSendClicked = {
	event: 'email_send_clicked';
	event_attributes: {
		duration: number;
	}
}
export type EmailSuccessScreenShown = { event: 'email_success_screen_shown'; }
export type EmailErrorScreenShown = { event: 'email_error_screen_shown'; }
export type EmailSuccessSendAgain = { event: 'email_success_send_again'; }
export type EmailSuccessUseDifferent = { event: 'email_success_use_different'; }
export type EmailErrorTryAgain = { event: 'email_error_try_again'; }

export type SmsScreenShown = { event: 'sms_screen_shown'; }
export type SmsSendClicked = {
	event: 'sms_send_clicked';
	event_attributes: {
		geoip_country: string;
		selected_country: string;
		duration: number;
	}
}
export type SmsSuccessScreenShown = { event: 'sms_success_screen_shown'; }
export type SmsErrorScreenShown = { event: 'sms_error_screen_shown'; }
export type SmsSuccessSendAgain = { event: 'sms_success_send_again'; }
export type SmsSuccessUseDifferent = { event: 'sms_success_use_different'; }
export type SmsErrorTryAgain = { event: 'sms_error_try_again'; }

export type MobileConnectedScreenShown = { event: 'mobile_connected_screen_shown'; }

export type CameraPermissionsScreenShown = { event: 'camera_permissions_screen_shown'; }
export type CameraAllowClicked = { event: 'camera_allow_clicked'; }
export type CameraDenyClicked = { event: 'camera_deny_clicked'; }
export type CameraPermissionsGranted = {
	event: 'camera_permissions_granted';
	event_attributes: {
		duration: number;
	}
}
export type CameraPermissionsRejected = {
	event: 'camera_permissions_rejected';
	event_attributes: {
		screen: string;
		error_type: string;
		duration: number;
	}
}
export type CameraRejectedScreenShown = { event: 'camera_rejected_screen_shown'; }
export type CameraRejectedRetryClicked = {
	event: 'camera_rejected_retry_clicked';
}
export type CameraRejectedExitClicked = {
	event: 'camera_rejected_exit_clicked';
}

export type CameraDeniedScreenShown = { event: 'camera_denied_screen_shown' }

export type CameraErrorScreenShown = { event: 'camera_error_screen_shown' }
export type CameraErrorRefreshClicked = {
	event: 'camera_error_refresh_clicked';
	event_attributes: {
		duration: number;
	}
}

export type PoorCameraQualityScreenShown = { event: 'poor_camera_quality_screen_shown' }
export type PoorCameraQualityExitClicked = {
	event: 'poor_camera_quality_exit_clicked';
	event_attributes: { duration: number }
}

export type DocFrontInstructionsScreenShown = { event: 'doc_front_instructions_screen_shown'; }
export type DocFrontInstructionsScreenContinue = {
	event: 'doc_front_instructions_screen_continue';
	event_attributes: { duration: number }
}
export type DocFrontUploadSuccess = {
	event: 'doc_front_upload_success';
	event_attributes: {
		upload_time: number;
	}
}
export type DocFrontFeedbackScreenShown = {
	event: 'doc_front_feedback_screen_shown';
	event_attributes: {
		can_continue: boolean;
		error_code: string[];
	}
}
export type DocFrontFeedbackRetry = {
	event: 'doc_front_feedback_retry';
	event_attributes: {
		can_continue: boolean;
		duration: number;
	}
}
export type DocFrontFeedbackContinue = {
	event: 'doc_front_feedback_continue';
	event_attributes: {
		can_continue: boolean;
		duration: number;
	}
}

export type DocBackInstructionsScreenShown = { event: 'doc_back_instructions_screen_shown'; }
export type DocBackInstructionsScreenContinue = {
	event: 'doc_back_instructions_screen_continue';
	event_attributes: {
		duration: number;
	}
}
export type DocBackUploadSuccess = {
	event: 'doc_back_upload_success';
	event_attributes: {
		upload_time: number;
	};
}
export type DocBackFeedbackScreenShown = {
	event: 'doc_back_feedback_screen_shown';
	event_attributes: {
		can_continue: boolean;
		error_code: string[];
	}
}
export type DocBackFeedbackRetry = {
	event: 'doc_back_feedback_retry';
	event_attributes: {
		can_continue: boolean;
	}
}
export type DocBackFeedbackContinue = {
	event: 'doc_back_feedback_continue';
	event_attributes: {
		can_continue: boolean;
	}
}

export type SelfieInstructionsScreenShown = { event: 'selfie_instructions_screen_shown'; }
export type SelfieInstructionsScreenContinue = {
	event: 'selfie_instructions_screen_continue';
	event_attributes: {
		duration: number;
	}
}
export type SelfieUploadSuccess = {
	event: 'selfie_upload_success';
	event_attributes: {
		upload_time: number;
	}
}
export type SelfieFeedbackScreenShown = {
	event: 'selfie_feedback_screen_shown';
	event_attributes: {
		can_continue: boolean;
		error_code: string[];
	}
}
export type SelfieFeedbackRetry = {
	event: 'selfie_feedback_retry';
	event_attributes: {
		can_continue: boolean;
	}
}
export type SelfieFeedbackContinue = {
	event: 'selfie_feedback_continue';
	event_attributes: {
		can_continue: boolean;
	}
}

export type FlowCompletedScreenShown = { event: 'flow_completed_screen_shown' }
export type FlowCompletedFinish = {
	event: 'flow_completed_finish',
	event_attributes: {
		duration: number;
	}
}

export type LanguageSelectOpened = { event: 'language_select_opened' }
export type LanguageSelected = {
	event: 'language_selected';
	event_attributes: {
		previous: string;
		current: string;
	}
}

export type QuitSessionOpened = {
	event: 'quit_session_opened';
	event_attributes: {
		screen: string;
	}
}
export type QuitSessionCancelled = {
	event: 'quit_session_cancelled';
	event_attributes: {
		screen: string;
	}
}
export type QuitSessionConfirmed = {
	event: 'quit_session_confirmed';
	event_attributes: {
		screen: string;
	}
}

export type InvalidSessionScreenShown = {
	event: 'invalid_session_screen_shown';
	event_attributes: {
		type: 'desktop' | 'mobile'
	}
}
export type InvalidSessionRestart = {
	event: 'invalid_session_restart';
	event_attributes: {
		type: 'desktop' | 'mobile',
		duration: number;
	}
}
export type UnhandledErrorScreenShown = {
	event: 'unhandled_error_screen_shown';
	event_attributes: {
		type: 'desktop' | 'mobile'
		last_screen: string;
	}
}
export type InvalidDeviceScreenShown = { event: 'invalid_device_screen_shown' }
export type HiveLinkOpened = {
	event: 'hive_link_opened';
	event_attributes: {
		screen: string;
	}
}

export type AnalyticsEvent = WidgetInitializedEvent |
	WelcomeScreenShown |
	WelcomeScreenContinue |
	QrScreenShown |
	LinkScreenShown |
	LinkCopied |
	EmailScreenShown |
	EmailSendClicked |
	EmailSuccessScreenShown |
	EmailErrorScreenShown |
	EmailSuccessSendAgain |
	EmailSuccessUseDifferent |
	EmailErrorTryAgain |
	SmsScreenShown |
	SmsSendClicked |
	SmsSuccessScreenShown |
	SmsErrorScreenShown |
	SmsSuccessSendAgain |
	SmsSuccessUseDifferent |
	SmsErrorTryAgain |
	MobileConnectedScreenShown |
	CameraPermissionsScreenShown |
	CameraAllowClicked |
	CameraDenyClicked |
	CameraPermissionsGranted |
	CameraPermissionsRejected |
	CameraRejectedScreenShown |
	CameraRejectedRetryClicked |
	CameraRejectedExitClicked |
	DocFrontInstructionsScreenShown |
	DocFrontInstructionsScreenContinue |
	DocFrontUploadSuccess |
	DocFrontFeedbackScreenShown |
	DocFrontFeedbackRetry |
	DocFrontFeedbackContinue |
	DocBackInstructionsScreenShown |
	DocBackInstructionsScreenContinue |
	DocBackUploadSuccess |
	DocBackFeedbackScreenShown |
	DocBackFeedbackRetry |
	DocBackFeedbackContinue |
	SelfieInstructionsScreenShown |
	SelfieInstructionsScreenContinue |
	SelfieUploadSuccess |
	SelfieFeedbackScreenShown |
	SelfieFeedbackRetry |
	SelfieFeedbackContinue |
	FlowCompletedScreenShown |
	FlowCompletedFinish |
	LanguageSelectOpened |
	LanguageSelected |
	QuitSessionOpened |
	QuitSessionCancelled |
	QuitSessionConfirmed |
	InvalidSessionScreenShown |
	InvalidSessionRestart |
	UnhandledErrorScreenShown |
	InvalidDeviceScreenShown |
	HiveLinkOpened |
	PoorCameraQualityExitClicked |
	CameraErrorRefreshClicked |
	CameraDeniedScreenShown |
	CameraErrorScreenShown |
	PoorCameraQualityScreenShown;

type SendEventPayload = {
	event: string;
	event_type: string,
	event_timestamp: number;
	session_id: string;
	event_attributes?: { [key: string]: string };
};
type SendPayload = { event: SendEventPayload, baseUrl: string, applicationKey: string };

const sendEventFx = RootDomain.effect<SendPayload, void>({
	handler: async (payload) => {
		await request({
			method: 'POST',
			baseUrl: payload.baseUrl,
			url: `/sessions/${payload.event.session_id}/events`,
			headers: {
				'hive-application-key': payload.applicationKey,
			},
			body: payload.event,
		});
	},
});

const flowStartTime$ = RootDomain.store<number | null>(null)
	.on(sendEventFx.done, (time, payload) => {
		return payload.params.event.event === 'welcome_screen_shown' ? payload.params.event.event_timestamp : time;
	});
const stepsCount$ = RootDomain.store<number>(0)
	.on(sendEventFx.done, (count) => count + 1);

export const sendEvent = RootDomain.event<AnalyticsEvent>();

guard({
	source: sample({
		source: {
			baseUrls: baseUrls$,
			sessionConfig: sessionConfig$,
			applicationKey: applicationKey$,
			startTime: flowStartTime$,
			stepsCount: stepsCount$,
		},
		clock: sendEvent,
		fn: (source, clock): SendPayload => {
			if (!source.sessionConfig) { return null; }
			const event_attributes = clock.event_attributes || {};
			const event_timestamp = parseInt((Date.now() / 1000) + '');

			if (clock.event === 'flow_completed_finish') {
				event_attributes.mobile = {
					steps_count: source.stepsCount + 1,
					time_elapsed: event_timestamp - source.startTime,
				};
			}
			const event = {
				event: clock.event,
				event_timestamp,
				session_id: source.sessionConfig.id,
				event_type: 'sdk',
				event_attributes,
			};
			console.log(event);

			return {
				event,
				baseUrl: source.baseUrls.apiBaseUrl,
				applicationKey: source.applicationKey,
			};
		},
	}),
	filter: payload => payload !== null,
	target: sendEventFx,
});
